import { baseApi, TAGS } from 'shared/api';
import type { BaseResponse } from 'shared/models/common';
import buildFilesFormData from 'shared/utils/buildFilesFormData';

import type {
  CreateFormOptions,
  CreateKeyOrderFormRequest,
  KeyOrderCase,
} from './types';

export const keyOrdersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getKeyOrdersList: build.query<KeyOrderCase[], void>({
      query: () => ({
        url: '/user/cases/keyorder',
        method: 'GET',
      }),
      transformResponse: (response: BaseResponse<KeyOrderCase[]>) =>
        response.Data,
      providesTags: [TAGS.KEY_ORDERS],
    }),
    getKeyOrderCase: build.query<KeyOrderCase, string>({
      query: (keyOrderId) => ({
        url: `/user/cases/keyorder/${keyOrderId}`,
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      transformResponse: (response: BaseResponse<KeyOrderCase>) =>
        response.Data,
    }),
    getCreateFormOptions: build.query<CreateFormOptions, string>({
      query: (coopId) => ({
        url: `/user/cases/keyorder/form/create`,
        method: 'GET',
        params: {
          CooperativeId: coopId,
        },
      }),
      transformResponse: (response: BaseResponse<CreateFormOptions>) =>
        response.Data,
    }),
    createKeyOrderCase: build.mutation<
      KeyOrderCase,
      { body: CreateKeyOrderFormRequest; cooperativeId: string; files: File[] }
    >({
      queryFn: async (
        { body, cooperativeId, files },
        _queryApi,
        _extra,
        fetchWithBQ
      ) => {
        const createKeyOrderCaseResult = await fetchWithBQ({
          url: '/user/cases/keyorder',
          method: 'POST',
          body,
        });

        if (createKeyOrderCaseResult.error) {
          return { error: createKeyOrderCaseResult.error };
        }

        const { Data: keyOrderCase } =
          createKeyOrderCaseResult.data as BaseResponse<KeyOrderCase>;

        if (files.length) {
          const uploadKeyOrderFilesResult = await fetchWithBQ({
            url: `/user/cooperatives/${cooperativeId}/documents/incident/${keyOrderCase.Id}`,
            method: 'POST',
            body: buildFilesFormData(files),
          });

          if (uploadKeyOrderFilesResult.error) {
            const { Message } = uploadKeyOrderFilesResult.error.data as {
              Message: string;
            };
            const warning = `Selected files were not uploaded: ${Message}`;
            console.warn(warning);
          }
        }

        return { data: keyOrderCase };
      },
      invalidatesTags: (_, error) => (error ? [] : [TAGS.KEY_ORDERS]),
    }),
  }),
});
